import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Save, 
  Plus, 
  Trash2, 
  GripVertical, 
  Eye, 
  EyeOff,
  Megaphone,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Emoji options for announcements
const EMOJI_OPTIONS = ['🚚', '✨', '💎', '🎁', '💳', '🔥', '⭐', '💜', '🌟', '📦', '🎉', '💫', '🏷️', '❤️', '✅'];

const AdminAnnouncements = () => {
  const [messages, setMessages] = useState([]);
  const [speed, setSpeed] = useState(50);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/announcements`);
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
        setSpeed(data.speed || 50);
      }
    } catch (error) {
      console.error('Error loading announcements:', error);
      toast.error('Error al cargar los anuncios');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/announcements`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages,
          speed,
          background_color: 'dark',
          text_color: 'cyan'
        })
      });

      if (response.ok) {
        toast.success('Anuncios guardados correctamente');
      } else {
        throw new Error('Error al guardar');
      }
    } catch (error) {
      console.error('Error saving announcements:', error);
      toast.error('Error al guardar los anuncios');
    } finally {
      setSaving(false);
    }
  };

  const addMessage = () => {
    setMessages([
      ...messages,
      { text: 'Nuevo anuncio', icon: '✨', is_active: true }
    ]);
  };

  const removeMessage = (index) => {
    setMessages(messages.filter((_, i) => i !== index));
  };

  const updateMessage = (index, field, value) => {
    const updated = [...messages];
    updated[index] = { ...updated[index], [field]: value };
    setMessages(updated);
  };

  const toggleActive = (index) => {
    const updated = [...messages];
    updated[index] = { ...updated[index], is_active: !updated[index].is_active };
    setMessages(updated);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-500/20 rounded-xl">
              <Megaphone className="w-8 h-8 text-amber-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Cintillo de Anuncios</h1>
              <p className="text-gray-400 text-sm">Administra los mensajes del marquee</p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-lg transition-colors disabled:opacity-50"
          >
            <Save size={20} />
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>

        {/* Preview */}
        <div className="mb-8 rounded-xl overflow-hidden border border-gray-700">
          <div className="bg-gray-800 px-4 py-2 border-b border-gray-700">
            <span className="text-gray-400 text-sm flex items-center gap-2">
              <Eye size={16} /> Vista previa
            </span>
          </div>
          <div className="bg-neon-darker py-3 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-50" />
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-pink-500 to-transparent opacity-50" />
            <div className="flex items-center justify-center gap-8 overflow-hidden">
              {messages.filter(m => m.is_active).slice(0, 3).map((msg, i) => (
                <div key={i} className="flex items-center gap-2 whitespace-nowrap">
                  <span className="text-lg">{msg.icon}</span>
                  <span className="text-sm bg-gradient-to-r from-cyan-400 via-white to-pink-400 bg-clip-text text-transparent font-medium">
                    {msg.text}
                  </span>
                  {i < 2 && <span className="ml-6 text-cyan-500/50">◆</span>}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Speed Control */}
        <div className="bg-gray-800 rounded-xl p-6 mb-6 border border-gray-700">
          <label className="block text-white font-medium mb-3">
            Velocidad del marquee
          </label>
          <div className="flex items-center gap-4">
            <span className="text-gray-400 text-sm">Lento</span>
            <input
              type="range"
              min="20"
              max="100"
              value={speed}
              onChange={(e) => setSpeed(parseInt(e.target.value))}
              className="flex-1 accent-amber-500"
            />
            <span className="text-gray-400 text-sm">Rápido</span>
            <span className="text-amber-500 font-mono w-12">{speed}px/s</span>
          </div>
        </div>

        {/* Messages List */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
            <h2 className="text-white font-semibold">Mensajes ({messages.length})</h2>
            <button
              onClick={addMessage}
              className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              <Plus size={18} />
              Agregar mensaje
            </button>
          </div>

          <div className="divide-y divide-gray-700">
            {messages.map((msg, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 ${!msg.is_active ? 'opacity-50' : ''}`}
              >
                <div className="flex items-start gap-4">
                  {/* Drag handle */}
                  <div className="pt-3 text-gray-500 cursor-move">
                    <GripVertical size={20} />
                  </div>

                  {/* Emoji selector */}
                  <div className="flex-shrink-0">
                    <label className="block text-gray-400 text-xs mb-1">Ícono</label>
                    <select
                      value={msg.icon}
                      onChange={(e) => updateMessage(index, 'icon', e.target.value)}
                      className="bg-gray-700 border border-gray-600 rounded-lg p-2 text-xl cursor-pointer"
                    >
                      {EMOJI_OPTIONS.map(emoji => (
                        <option key={emoji} value={emoji}>{emoji}</option>
                      ))}
                    </select>
                  </div>

                  {/* Text input */}
                  <div className="flex-1">
                    <label className="block text-gray-400 text-xs mb-1">Mensaje</label>
                    <input
                      type="text"
                      value={msg.text}
                      onChange={(e) => updateMessage(index, 'text', e.target.value)}
                      placeholder="Escribe tu anuncio..."
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-amber-500 focus:outline-none"
                    />
                  </div>

                  {/* Toggle active */}
                  <button
                    onClick={() => toggleActive(index)}
                    className={`flex-shrink-0 p-2 rounded-lg transition-colors ${
                      msg.is_active 
                        ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                        : 'bg-gray-700 text-gray-500 hover:bg-gray-600'
                    }`}
                    title={msg.is_active ? 'Activo' : 'Inactivo'}
                  >
                    {msg.is_active ? <Eye size={20} /> : <EyeOff size={20} />}
                  </button>

                  {/* Delete */}
                  <button
                    onClick={() => removeMessage(index)}
                    className="flex-shrink-0 p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </motion.div>
            ))}

            {messages.length === 0 && (
              <div className="p-12 text-center">
                <Sparkles className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No hay mensajes configurados</p>
                <button
                  onClick={addMessage}
                  className="mt-4 text-amber-500 hover:text-amber-400"
                >
                  Agregar el primer mensaje
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tips */}
        <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <h3 className="text-amber-500 font-medium mb-2 flex items-center gap-2">
            <Sparkles size={18} />
            Consejos
          </h3>
          <ul className="text-gray-400 text-sm space-y-1">
            <li>• Mantén los mensajes cortos y directos (máx. 60 caracteres)</li>
            <li>• Usa emojis para llamar la atención</li>
            <li>• Promociones, envío gratis y garantías funcionan muy bien</li>
            <li>• Puedes desactivar mensajes sin eliminarlos</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminAnnouncements;
